import { msg } from '@lingui/core/macro';

import { moneyCentsParam } from '@/shared/filters/dates';
import { defineFilters } from '@/shared/filters/define-filters';
import { defineEntitySource } from '@/shared/form/entity-source';
import { formatDocument } from '@/shared/format/document';
import { percentToFraction } from '@/shared/format/percent';
import { getCurrentTenant } from '@/shared/http/client';

import { CLOSURE_STATUS, CREDIT_NOTE_STATUS, LIQUIDATION_MODE, PRODUCT_CATEGORY, YES_NO } from './credit-note.status';
import type { FundOption, PersonOption, ProductOption } from './credit-note.wire';

// Filtros da lista declarados uma vez (P01; discovery/02 §2.3; CreditNoteFilter.tsx). Arquivo leve: a
// rota usa o `shape` no `validateSearch`, então o fio dos autocompletes entra por import dinâmico.

const lookups = () => import('./credit-note.api');

/** Produto: `GET /Product`; root vê `nome (correspondente)` (CreditNoteFilter.tsx:118-131). */
export const productSource = defineEntitySource<ProductOption>({
  key: ['credit-note', 'filter', 'product'],
  scope: () => getCurrentTenant() ?? null,
  search: async (query, signal) => (await lookups()).listProductOptions(query || undefined, signal),
  toOption: (item, i18n) => ({
    value: item.id,
    label: item.name ?? i18n._(msg`Não informado`),
    ...(item.tenantDisplay ? { description: i18n._(msg`Parceiro: ${item.tenantDisplay}`) } : {}),
  }),
});

/** Tomador: `GET /Person` com CPF/CNPJ e parceiro (personContext.tsx:74-93). */
export const personSource = defineEntitySource<PersonOption>({
  key: ['credit-note', 'filter', 'person'],
  scope: () => getCurrentTenant() ?? null,
  search: async (query, signal) => (await lookups()).listPersonOptions(query || undefined, signal),
  toOption: (item, i18n) => {
    const document = item.registrationNumber ? (formatDocument(item.registrationNumber) ?? item.registrationNumber) : undefined;
    const parts = [document ? i18n._(msg`CPF/CNPJ: ${document}`) : undefined, item.tenantDisplay ? i18n._(msg`Parceiro: ${item.tenantDisplay}`) : undefined].filter(Boolean);
    return { value: item.id, label: item.name ?? item.id, ...(parts.length > 0 ? { description: parts.join(' · ') } : {}) };
  },
});

/** Fundo (cessionário): `GET /Fund` (CreditNoteFilter.tsx:103-116). */
export const fundSource = defineEntitySource<FundOption>({
  key: ['credit-note', 'filter', 'fund'],
  scope: () => getCurrentTenant() ?? null,
  search: async (query, signal) => (await lookups()).listFundOptions(query || undefined, signal),
  toOption: (item, i18n) => ({
    value: item.id,
    label: item.name ?? i18n._(msg`Não informado`),
    ...(item.tenantDisplay ? { description: i18n._(msg`Parceiro: ${item.tenantDisplay}`) } : {}),
  }),
});

/** `PercentageFormField` grava a fração (`formatDivide`, PercentageFormField.tsx:101-104): "1,99" → "0.0199". */
export function percentFilterParam(value: string): string | undefined {
  const normalized = value.replace('%', '').trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return undefined;
  return percentToFraction(normalized);
}

/** Número da operação: o campo tem o prefixo `#` fora do valor (CreditNoteFilter.tsx:288-306). */
export function creditNoteNoParam(value: string): string | undefined {
  const digits = value.replace(/\D/g, '');
  return digits === '' ? undefined : digits;
}

/**
 * Data de pagamento e vencimento dos itens: o legado manda `new Date(valor).toISOString()` do dia
 * escolhido (filterContext.tsx:131-134), a meia-noite de São Paulo em UTC. O fim continua
 * `formatFinalDateWithTimeZone` (o padrão do período).
 */
export function dayStartIsoParam(day: string): string {
  return new Date(`${day}T00:00:00-03:00`).toISOString();
}

/** Na ordem alfabética do "Filtrar por" do legado; Status e Data de início fixados como no protótipo. */
export const creditNoteFilters = defineFilters([
  { key: 'searchString', type: 'search', label: msg`Todos`, placeholder: msg`Buscar por número, tomador ou CPF/CNPJ` },
  { key: 'ownCompliance', type: 'select', label: msg`Aprovação compliance própria`, options: YES_NO },
  { key: 'productCategoryDisplayList', type: 'multiselect', label: msg`Categoria do produto`, options: PRODUCT_CATEGORY },
  { key: 'initialDate', to: 'finalDate', type: 'dateRange', label: msg`Data de início`, fromLabel: msg`Data inicial`, toLabel: msg`Data final`, pinned: true },
  {
    key: 'initialPaymentDate',
    to: 'finalPaymentDate',
    type: 'dateRange',
    label: msg`Data de pagamento`,
    fromLabel: msg`Data inicial do pagamento`,
    toLabel: msg`Data final do pagamento`,
    fromParam: dayStartIsoParam,
  },
  { key: 'fundId', type: 'entity', label: msg`Fundo`, source: fundSource, placeholder: msg`Buscar fundo` },
  { key: 'ownLiquidation', type: 'select', label: msg`Liquidação própria`, options: YES_NO },
  { key: 'liquidationModeList', type: 'multiselect', label: msg`Modo de liquidação`, options: LIQUIDATION_MODE },
  { key: 'creditNoteNo', type: 'text', label: msg`Número da operação`, placeholder: msg`#48213`, max: 12, toParam: creditNoteNoParam },
  { key: 'productIdList', type: 'entity', label: msg`Produto`, source: productSource, multiple: true, placeholder: msg`Buscar produto` },
  { key: 'insurance', type: 'select', label: msg`Seguro cancelado`, options: YES_NO },
  { key: 'status', type: 'status', label: msg`Status`, options: CREDIT_NOTE_STATUS, pinned: true },
  { key: 'closureStatus', type: 'select', label: msg`Status do roteiro de liquidação`, options: CLOSURE_STATUS },
  { key: 'maxOpApr', type: 'text', label: msg`Taxa máxima`, placeholder: msg`Em %, como 2,49`, max: 12, toParam: percentFilterParam },
  { key: 'minOpApr', type: 'text', label: msg`Taxa mínima`, placeholder: msg`Em %, como 1,99`, max: 12, toParam: percentFilterParam },
  { key: 'personIdList', type: 'entity', label: msg`Tomador`, source: personSource, multiple: true, placeholder: msg`Buscar tomador` },
  { key: 'maxValue', type: 'text', label: msg`Valor máximo`, placeholder: msg`Em reais, como 50.000,00`, max: 20, toParam: moneyCentsParam },
  { key: 'minValue', type: 'text', label: msg`Valor mínimo`, placeholder: msg`Em reais, como 1.000,00`, max: 20, toParam: moneyCentsParam },
  {
    key: 'initialScheduleDueDate',
    to: 'finalScheduleDueDate',
    type: 'dateRange',
    label: msg`Vencimento dos itens do roteiro`,
    fromLabel: msg`Data inicial do vencimento dos itens`,
    toLabel: msg`Data final do vencimento dos itens`,
    fromParam: dayStartIsoParam,
  },
]);

/** "Seguro cancelado" só para o tenant raiz (CreditNoteFilter.tsx:64-66). */
export const creditNoteTenantFilters = creditNoteFilters.pick(...creditNoteFilters.fields.filter((field) => field.key !== 'insurance').map((field) => field.key));

/** Exportar exige um período completo de início ou de vencimento dos itens (CreditNoteListButtons.tsx:134-151). */
export function hasExportDates(search: Readonly<Record<string, unknown>>): boolean {
  const filled = (key: string) => typeof search[key] === 'string' && search[key] !== '';
  return (filled('initialDate') && filled('finalDate')) || (filled('initialScheduleDueDate') && filled('finalScheduleDueDate'));
}
